import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { produce } from 'immer';

import {
  TABLE_RESERVATIONS_STATE_TOKEN,
  TableReservationsStateModel
} from './table-reservation.model.state';
import {
  CreateTableReservation,
  CreateTableReservationError,
  CreateTableReservationSuccess,
  DeleteTableReservation,
  DeleteTableReservationError,
  DeleteTableReservationSuccess,
  EditTableReservation,
  EditTableReservationError,
  EditTableReservationSuccess,
  GetTableReservations,
  GetTableReservationsError,
  GetTableReservationsSuccess,
  SetDinerSearchTableReservations,
  SetReservationViewFocus
} from './table-reservation.action';
import { DiningService } from 'src/app/services';
import { ReservationViewFocus, TableReservation } from 'src/app/models';
import { DateHelper } from 'src/app/helpers';
import { LocationSelectors } from '../../location/location.selectors';

@State({
  name: TABLE_RESERVATIONS_STATE_TOKEN,
  defaults: {
    date: DateHelper.format(new Date()),
    diner_name: '',
    reservations: [],
    dinerSearchReservations: [],
    reservationViewFocus: ReservationViewFocus.LIST
  }
})
@Injectable()
export class TableReservationsState {
  constructor(
    private readonly diningService: DiningService,
    private readonly store: Store
  ) {}

  @Action(GetTableReservations, { cancelUncompleted: true })
  getTableReservations(
    ctx: StateContext<TableReservationsStateModel>,
    { date, diner_name }: GetTableReservations
  ) {
    ctx.patchState({ date, diner_name });

    return this.diningService.getTableReservations(date, diner_name).pipe(
      tap((res) =>
        ctx.dispatch(
          new GetTableReservationsSuccess(res.reservations, !!diner_name)
        )
      ),
      catchError((error: HttpErrorResponse) =>
        ctx.dispatch(new GetTableReservationsError(error))
      )
    );
  }

  @Action(GetTableReservationsSuccess)
  getTableReservationsSuccess(
    ctx: StateContext<TableReservationsStateModel>,
    { reservations, isDinerSearching }: GetTableReservationsSuccess
  ) {
    if (isDinerSearching) {
      return ctx.patchState({ dinerSearchReservations: reservations });
    }
    ctx.patchState({ reservations });
  }

  @Action(SetDinerSearchTableReservations)
  setDinerSearchTableReservations(
    ctx: StateContext<TableReservationsStateModel>,
    { reservations }: SetDinerSearchTableReservations
  ) {
    ctx.patchState({ dinerSearchReservations: reservations });
  }

  @Action(CreateTableReservation)
  createTableReservations(
    ctx: StateContext<TableReservationsStateModel>,
    { dto }: CreateTableReservation
  ) {
    return this.diningService.createTableReservation(dto).pipe(
      tap((res) => ctx.dispatch(new CreateTableReservationSuccess(res))),
      catchError((error: HttpErrorResponse) =>
        ctx.dispatch(new CreateTableReservationError(error))
      )
    );
  }

  @Action(CreateTableReservationSuccess)
  createTableReservationSuccess(
    ctx: StateContext<TableReservationsStateModel>,
    { reservation }: CreateTableReservationSuccess
  ) {
    const { date, diner_name } = ctx.getState();

    if (
      !!diner_name &&
      reservation.reservation_diners.some((diner) =>
        diner.diner_name
          .toLocaleLowerCase()
          .includes(diner_name.toLocaleLowerCase())
      )
    ) {
      return ctx.setState(
        patch({
          dinerSearchReservations: insertItem(reservation)
        })
      );
    }

    if (date === reservation.date) {
      return ctx.setState(
        patch({
          reservations: insertItem(reservation)
        })
      );
    }
  }

  @Action(EditTableReservation)
  editTableReservation(
    ctx: StateContext<TableReservationsStateModel>,
    { id, dto }: EditTableReservation
  ) {
    return this.diningService.editTableReservation(id, dto).pipe(
      tap((res) => ctx.dispatch(new EditTableReservationSuccess(res))),
      catchError((error: HttpErrorResponse) =>
        ctx.dispatch(new EditTableReservationError(error))
      )
    );
  }

  @Action(EditTableReservationSuccess)
  editTableReservationSuccess(
    ctx: StateContext<TableReservationsStateModel>,
    { reservation }: EditTableReservationSuccess
  ) {
    const location = this.store.selectSnapshot(LocationSelectors.getLocation);

    if (location.id !== reservation.pos_location_id) {
      ctx.setState(
        patch({
          reservations: removeItem((item) => item.id === reservation.id)
        })
      );

      return;
    }

    ctx.setState(
      produce((draft) => {
        if (draft.diner_name) {
          const idx = draft.dinerSearchReservations.findIndex(
            (item) => item.id === reservation.id
          );
          if (idx > -1) {
            draft.dinerSearchReservations[idx] = {
              ...draft.dinerSearchReservations[idx],
              ...reservation
            };
          }
        } else {
          const idx = draft.reservations.findIndex(
            (item) => item.id === reservation.id
          );
          if (idx > -1) {
            draft.reservations[idx] = {
              ...draft.reservations[idx],
              ...reservation
            };
          } else if (reservation.date === draft.date) {
            draft.reservations.push(reservation);
          }
          draft.reservations = draft.reservations.sort((a, b) =>
            this.sortReservations(a, b)
          );
        }
      })
    );
  }

  private sortReservations = (a: TableReservation, b: TableReservation) => {
    if (a.meal_id === b.meal_id) {
      return (
        DateHelper.parse(a.start_time).getTime() -
        DateHelper.parse(b.start_time).getTime()
      );
    }

    return a.meal_id - b.meal_id;
  };

  @Action(DeleteTableReservation)
  deleteTableReservation(
    ctx: StateContext<TableReservationsStateModel>,
    { reservation }: DeleteTableReservation
  ) {
    return this.diningService.deleteTableReservation(reservation.id).pipe(
      tap(() =>
        ctx.dispatch(new DeleteTableReservationSuccess(reservation.id))
      ),
      catchError((error: HttpErrorResponse) =>
        ctx.dispatch(new DeleteTableReservationError(error))
      )
    );
  }

  @Action(DeleteTableReservationSuccess)
  deleteTableReservationSuccess(
    ctx: StateContext<TableReservationsStateModel>,
    { id }: DeleteTableReservationSuccess
  ) {
    ctx.setState(
      patch({
        reservations: removeItem((item) => item.id === id)
      })
    );
  }

  @Action(SetReservationViewFocus)
  setReservationViewFocus(
    { patchState }: StateContext<TableReservationsStateModel>,
    { view }: SetReservationViewFocus
  ) {
    patchState({ reservationViewFocus: view });
  }
}
